import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Server,
  FileCheck,
  ListTodo,
  AlertTriangle,
  ShieldAlert,
  ArrowRight,
  Clock,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Activity,
  Calendar,
  ClipboardCheck,
  Bell,
  Heart
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import api, { dashboardApi, seedData } from '../lib/api';
import { toast } from 'sonner';
import { UpcomingReviewsWidget } from '../components/UpcomingReviewsWidget';
import { 
  criticalityChartColors, 
  formatNumber, 
  formatPercentage,
  cn
} from '../lib/utils';

export default function Dashboard() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSeeding, setIsSeeding] = useState(false);
  const [data, setData] = useState(null);
  const [healthData, setHealthData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [dashboardRes, healthRes] = await Promise.all([
        dashboardApi.getSummary(),
        api.get('/system-health/summary')
      ]);
      setData(dashboardRes.data);
      setHealthData(healthRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      if (error.response?.status === 401) return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeedData = async () => {
    setIsSeeding(true);
    try {
      await seedData();
      toast.success('Dados iniciais criados com sucesso');
      await loadDashboardData();
    } catch (error) {
      if (error.response?.data?.message?.includes('já existem')) {
        toast.info('Os dados já existem');
        await loadDashboardData();
      } else {
        toast.error('Erro ao criar dados iniciais');
      }
    } finally {
      setIsSeeding(false);
    }
  };

  if (isLoading) {
    return (
      <Layout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  if (!data || data.assets?.total === 0) {
    return (
      <Layout title="Dashboard">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Server className="w-12 h-12 text-slate-300 mb-4" />
          <h2 className="text-lg font-semibold text-slate-700 mb-2">Sem dados</h2>
          <p className="text-sm text-slate-500 mb-4">
            Clique no botão abaixo para carregar os dados iniciais
          </p>
          <Button onClick={handleSeedData} disabled={isSeeding} data-testid="seed-data-btn">
            {isSeeding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Carregar Dados Iniciais
          </Button>
        </div>
      </Layout>
    );
  }

  const complianceScore = data.compliance?.percentage || 0;
  const healthScore = healthData?.health_score || 0;
  const healthStatus = healthData?.health_status || 'unknown';

  const criticalityData = [
    { name: 'Crítica', value: data.assets?.by_criticality?.critica || 0, color: criticalityChartColors.critica },
    { name: 'Alta', value: data.assets?.by_criticality?.alta || 0, color: criticalityChartColors.alta },
    { name: 'Média', value: data.assets?.by_criticality?.media || 0, color: criticalityChartColors.media },
    { name: 'Baixa', value: data.assets?.by_criticality?.baixa || 0, color: criticalityChartColors.baixa },
  ].filter(d => d.value > 0);

  const getHealthColor = (status) => {
    const colors = {
      healthy: 'text-green-600 bg-green-50 border-green-200',
      warning: 'text-yellow-600 bg-yellow-50 border-yellow-200',
      degraded: 'text-orange-600 bg-orange-50 border-orange-200',
      critical: 'text-red-600 bg-red-50 border-red-200'
    };
    return colors[status] || colors.warning;
  };

  const getHealthIcon = (status) => {
    if (status === 'healthy') return <CheckCircle className="h-6 w-6" />;
    if (status === 'warning') return <AlertCircle className="h-6 w-6" />;
    if (status === 'critical') return <XCircle className="h-6 w-6" />;
    return <AlertTriangle className="h-6 w-6" />;
  };

  return (
    <Layout title="Dashboard">
      <div className="space-y-6" data-testid="dashboard-container">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 font-heading">
              Olá, {user?.name?.split(' ')[0] || 'Utilizador'}
            </h2>
            <p className="text-slate-500">
              Aqui está o resumo da conformidade NIS2 da sua organização
            </p>
          </div>
          <Button asChild data-testid="view-reports-btn">
            <Link to="/reports">
              Ver Relatórios
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        {/* System Health Panel */}
        <Card className={cn("card-flat border-l-4", 
          healthStatus === 'healthy' ? 'border-l-green-500' :
          healthStatus === 'warning' ? 'border-l-yellow-500' :
          healthStatus === 'degraded' ? 'border-l-orange-500' :
          'border-l-red-500'
        )} data-testid="system-health-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Saúde do Sistema
              </CardTitle>
              <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full border", getHealthColor(healthStatus))}>
                {getHealthIcon(healthStatus)}
                <span className="font-semibold">{healthScore}%</span>
                <span className="text-sm">{healthData?.health_label}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {/* Expiring Assets */}
              <Link to="/assets" className="block">
                <div className={cn(
                  "p-3 rounded-lg border transition-colors hover:border-slate-300",
                  healthData?.alerts?.expiring_assets?.count > 0 ? "bg-orange-50 border-orange-200" : "bg-slate-50"
                )}>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className={cn("h-4 w-4", healthData?.alerts?.expiring_assets?.count > 0 ? "text-orange-600" : "text-slate-400")} />
                    <span className="text-xs font-medium text-slate-600">Ativos a Expirar</span>
                  </div>
                  <p className={cn("text-2xl font-bold", healthData?.alerts?.expiring_assets?.count > 0 ? "text-orange-600" : "text-slate-900")}>
                    {healthData?.alerts?.expiring_assets?.count || 0}
                  </p>
                </div>
              </Link>

              {/* Overdue Tasks */}
              <Link to="/tasks" className="block">
                <div className={cn(
                  "p-3 rounded-lg border transition-colors hover:border-slate-300",
                  healthData?.alerts?.overdue_tasks?.count > 0 ? "bg-red-50 border-red-200" : "bg-slate-50"
                )}>
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className={cn("h-4 w-4", healthData?.alerts?.overdue_tasks?.count > 0 ? "text-red-600" : "text-slate-400")} />
                    <span className="text-xs font-medium text-slate-600">Tarefas Atrasadas</span>
                  </div>
                  <p className={cn("text-2xl font-bold", healthData?.alerts?.overdue_tasks?.count > 0 ? "text-red-600" : "text-slate-900")}>
                    {healthData?.alerts?.overdue_tasks?.count || 0}
                  </p>
                </div>
              </Link>

              {/* Open Incidents */}
              <Link to="/incidents" className="block">
                <div className={cn(
                  "p-3 rounded-lg border transition-colors hover:border-slate-300",
                  healthData?.alerts?.open_incidents?.critical > 0 ? "bg-red-50 border-red-200" :
                  healthData?.alerts?.open_incidents?.count > 0 ? "bg-yellow-50 border-yellow-200" : "bg-slate-50"
                )}>
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className={cn("h-4 w-4", 
                      healthData?.alerts?.open_incidents?.critical > 0 ? "text-red-600" :
                      healthData?.alerts?.open_incidents?.count > 0 ? "text-yellow-600" : "text-slate-400"
                    )} />
                    <span className="text-xs font-medium text-slate-600">Incidentes Abertos</span>
                  </div>
                  <p className={cn("text-2xl font-bold", 
                    healthData?.alerts?.open_incidents?.critical > 0 ? "text-red-600" :
                    healthData?.alerts?.open_incidents?.count > 0 ? "text-yellow-600" : "text-slate-900"
                  )}>
                    {healthData?.alerts?.open_incidents?.count || 0}
                  </p>
                  {healthData?.alerts?.open_incidents?.critical > 0 && (
                    <p className="text-xs text-red-600">{healthData.alerts.open_incidents.critical} críticos</p>
                  )}
                </div>
              </Link>

              {/* Pending Approvals */}
              <Link to="/approvals" className="block">
                <div className={cn(
                  "p-3 rounded-lg border transition-colors hover:border-slate-300",
                  healthData?.alerts?.pending_approvals?.count > 0 ? "bg-blue-50 border-blue-200" : "bg-slate-50"
                )}>
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardCheck className={cn("h-4 w-4", healthData?.alerts?.pending_approvals?.count > 0 ? "text-blue-600" : "text-slate-400")} />
                    <span className="text-xs font-medium text-slate-600">Aprovações Pendentes</span>
                  </div>
                  <p className={cn("text-2xl font-bold", healthData?.alerts?.pending_approvals?.count > 0 ? "text-blue-600" : "text-slate-900")}>
                    {healthData?.alerts?.pending_approvals?.count || 0}
                  </p>
                </div>
              </Link>

              {/* High Risks */}
              <Link to="/risks" className="block">
                <div className={cn(
                  "p-3 rounded-lg border transition-colors hover:border-slate-300",
                  healthData?.alerts?.high_risks?.count > 0 ? "bg-purple-50 border-purple-200" : "bg-slate-50"
                )}>
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldAlert className={cn("h-4 w-4", healthData?.alerts?.high_risks?.count > 0 ? "text-purple-600" : "text-slate-400")} />
                    <span className="text-xs font-medium text-slate-600">Riscos Elevados</span>
                  </div>
                  <p className={cn("text-2xl font-bold", healthData?.alerts?.high_risks?.count > 0 ? "text-purple-600" : "text-slate-900")}>
                    {healthData?.alerts?.high_risks?.count || 0}
                  </p>
                </div>
              </Link>
            </div>

            {/* Alert Items */}
            {(healthData?.alerts?.open_incidents?.items?.length > 0 || 
              healthData?.alerts?.overdue_tasks?.items?.length > 0) && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Recent Incidents */}
                  {healthData?.alerts?.open_incidents?.items?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-red-500" />
                        Incidentes Recentes
                      </h4>
                      <div className="space-y-2">
                        {healthData.alerts.open_incidents.items.slice(0, 3).map((inc) => (
                          <Link key={inc.incident_id} to="/incidents" className="block p-2 bg-white rounded border hover:border-slate-300 transition-colors">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-900 truncate">{inc.title}</span>
                              <Badge variant="outline" className={cn("text-xs",
                                inc.severity === 'critico' ? 'bg-red-50 text-red-700 border-red-200' :
                                inc.severity === 'alto' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                'bg-yellow-50 text-yellow-700 border-yellow-200'
                              )}>{inc.severity}</Badge>
                            </div>
                            {inc.nis2_24h_expired && !inc.nis2_72h_expired && (
                              <p className="text-xs text-orange-600 mt-1">⚠️ Prazo 24h NIS2 expirado</p>
                            )}
                            {inc.nis2_72h_expired && (
                              <p className="text-xs text-red-600 mt-1">🚨 Prazo 72h NIS2 expirado</p>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Overdue Tasks */}
                  {healthData?.alerts?.overdue_tasks?.items?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-red-500" />
                        Tarefas em Atraso
                      </h4>
                      <div className="space-y-2">
                        {healthData.alerts.overdue_tasks.items.slice(0, 3).map((task) => (
                          <Link key={task.task_id} to="/tasks" className="block p-2 bg-white rounded border hover:border-slate-300 transition-colors">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-900 truncate">{task.title}</span>
                              <span className="text-xs text-red-600">{task.days_overdue}d atraso</span>
                            </div>
                            {task.assigned_to_name && (
                              <p className="text-xs text-slate-500 mt-1">Atribuído: {task.assigned_to_name}</p>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Reviews Widget — cross-module (Policies + Suppliers + DPIAs) */}
        <UpcomingReviewsWidget days={90} limit={5} />

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-flat" data-testid="assets-metric">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <Server className="h-8 w-8 text-blue-600" />
                <span className="text-2xl font-bold text-slate-900">
                  {formatNumber(data.assets?.total)}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Total de Ativos</p>
              <p className="text-xs text-red-600 mt-1">
                {data.assets?.critical} críticos
              </p>
            </CardContent>
          </Card>

          <Card className="card-flat" data-testid="compliance-metric">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <FileCheck className="h-8 w-8 text-green-600" />
                <span className="text-2xl font-bold text-slate-900">
                  {formatPercentage(complianceScore)}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Conformidade</p>
              <Progress value={complianceScore} className="mt-2 h-1.5" />
            </CardContent>
          </Card>

          <Card className="card-flat" data-testid="tasks-metric">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <ListTodo className="h-8 w-8 text-amber-600" />
                <span className="text-2xl font-bold text-slate-900">
                  {formatNumber(data.tasks?.total)}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Tarefas</p>
              <p className="text-xs text-red-600 mt-1">
                {data.tasks?.overdue} em atraso
              </p>
            </CardContent>
          </Card>

          <Card className="card-flat" data-testid="incidents-metric">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <span className="text-2xl font-bold text-slate-900">
                  {formatNumber(data.incidents?.open)}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2">Incidentes Abertos</p>
              <p className="text-xs text-orange-600 mt-1">
                {data.risks?.high} riscos elevados
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Compliance Status */}
          <Card className="card-flat" data-testid="compliance-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading">Estado de Conformidade</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center">
                <div className="relative w-40 h-40">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle className="text-slate-100" strokeWidth="12" stroke="currentColor" fill="transparent" r="60" cx="80" cy="80" />
                    <circle className="text-blue-600" strokeWidth="12" strokeDasharray={`${complianceScore * 3.77} 377`} strokeLinecap="round" stroke="currentColor" fill="transparent" r="60" cx="80" cy="80" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-3xl font-bold text-slate-900">{formatPercentage(complianceScore, 0)}</span>
                      <p className="text-xs text-slate-500">Conforme</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                <div>
                  <p className="text-lg font-semibold text-green-600">{data.compliance?.compliant || 0}</p>
                  <p className="text-xs text-slate-500">Conforme</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-amber-600">
                    {(data.compliance?.total_requirements || 0) - (data.compliance?.compliant || 0)}
                  </p>
                  <p className="text-xs text-slate-500">Pendente</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-600">{data.compliance?.total_requirements || 0}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assets by Criticality */}
          <Card className="card-flat" data-testid="criticality-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading">Ativos por Criticidade</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={criticalityData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {criticalityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="card-flat hover:border-blue-200 transition-colors cursor-pointer" data-testid="quick-action-assets">
            <Link to="/assets">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Server className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">Gerir Ativos</h3>
                  <p className="text-sm text-slate-500">Inventário e classificação</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </CardContent>
            </Link>
          </Card>

          <Card className="card-flat hover:border-blue-200 transition-colors cursor-pointer" data-testid="quick-action-tasks">
            <Link to="/tasks">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-amber-50 flex items-center justify-center">
                  <ListTodo className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">Tarefas Pendentes</h3>
                  <p className="text-sm text-slate-500">{data.tasks?.overdue} em atraso</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </CardContent>
            </Link>
          </Card>

          <Card className="card-flat hover:border-blue-200 transition-colors cursor-pointer" data-testid="quick-action-requirements">
            <Link to="/requirements">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <FileCheck className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-slate-900">Requisitos NIS2</h3>
                  <p className="text-sm text-slate-500">Ver catálogo completo</p>
                </div>
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </CardContent>
            </Link>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
